"use client";

import Link from "next/link";

import { useId, useState, useTransition } from "react";
import { useRouter } from "next/navigation";
import { ChevronDown, Trash2 } from "lucide-react";
import {
  buildCompanionAvailabilityPreviewWindow,
  parseCompanionAvailability,
} from "@/lib/companion/availability";
import {
  buildCompanionAvailabilityFitLine,
  type CompanionAvailabilityWindow,
} from "@/lib/companion/availability-fit";
import type { CompanionSourceState } from "@/lib/companion/source-state";
import {
  deleteCompanionAvailabilityWindow,
  saveCompanionAvailability,
  updateCompanionAvailabilityWindowActive,
} from "@/app/actions/companion-availability";

type Props = {
  windows: readonly CompanionAvailabilityWindow[];
  fitContext: { bestWindow: string; timeZone: string } | null;
  renderedAt: string;
  sourceState?: CompanionSourceState;
  surface?: "dark" | "light";
  readOnly?: boolean;
};

type FormStatus =
  | { kind: "idle"; message: string }
  | { kind: "success"; message: string }
  | { kind: "error"; message: string };

const SURFACE_CLASSES = {
  dark: {
    details: "border-white/12 bg-white/[0.05]",
    summary: "text-white focus-visible:ring-teal-light",
    eyebrow: "text-gold-light",
    icon: "text-white/58",
    body: "border-white/10",
    item: "border-white/10 bg-white/[0.06]",
    itemTitle: "text-white",
    kind: "border-white/12 bg-white/8 text-white/68",
    toggle: "border-white/12 text-white/78 focus-visible:ring-teal-light",
    delete: "border-rose-200/30 bg-rose-500/10 text-rose-50 focus-visible:ring-rose-200",
    empty: "border-white/10 bg-white/[0.04] text-white/62",
    label: "text-white/70",
    help: "border-white/10 bg-white/[0.04] text-white/62",
    idle: "text-white/58",
    success: "text-emerald-100",
    error: "text-rose-100",
  },
  light: {
    details: "border-border bg-warm-bg",
    summary: "text-navy focus-visible:ring-teal",
    eyebrow: "text-gold",
    icon: "text-text-muted",
    body: "border-border-light",
    item: "border-border-light bg-white",
    itemTitle: "text-navy",
    kind: "border-border bg-warm-bg text-gray-700",
    toggle: "border-border bg-white text-navy focus-visible:ring-teal",
    delete: "border-rose-200 bg-rose-50 text-rose-900 focus-visible:ring-rose-500",
    empty: "border-border-light bg-white text-text-muted",
    label: "text-navy",
    help: "border-border-light bg-white text-text-muted",
    idle: "text-text-muted",
    success: "text-emerald-700",
    error: "text-rose-700",
  },
} as const;

export function CompanionAvailabilityForm({
  windows,
  fitContext,
  renderedAt,
  sourceState,
  surface = "dark",
  readOnly = false,
}: Props) {
  const inputId = useId();
  const router = useRouter();
  const classes = SURFACE_CLASSES[surface];
  const [value, setValue] = useState("");
  const [status, setStatus] = useState<FormStatus>({
    kind: "idle",
    message: "Add the times you can actually go.",
  });
  const [pending, startTransition] = useTransition();

  function saveWindow() {
    if (readOnly) return;
    const parsed = parseCompanionAvailability(value);
    if (!parsed.ok) {
      setStatus({ kind: "error", message: parsed.error });
      return;
    }

    startTransition(async () => {
      const result = await saveCompanionAvailability(value);
      if (!result.ok) {
        setStatus({ kind: "error", message: result.error });
        return;
      }

      const fitLine = fitContext
        ? buildCompanionAvailabilityFitLine({
            windows: [buildCompanionAvailabilityPreviewWindow(parsed.availability)],
            bestWindow: fitContext.bestWindow,
            now: new Date(renderedAt),
            timeZone: fitContext.timeZone,
          })
        : null;
      setStatus({
        kind: "success",
        message: fitLine ? `Saved: ${result.label}. ${fitLine}` : `Saved: ${result.label}.`,
      });
      setValue("");
      queueAvailabilityRefresh(router);
    });
  }

  function toggleWindow(windowId: string | null, active: boolean) {
    if (readOnly) return;
    if (!windowId) {
      setStatus({ kind: "error", message: "Window not found." });
      return;
    }

    startTransition(async () => {
      const result = await updateCompanionAvailabilityWindowActive(windowId, active);
      if (!result.success) {
        setStatus({ kind: "error", message: result.error });
        return;
      }

      setStatus({
        kind: "success",
        message: active ? "Window is active." : "Window is paused.",
      });
      queueAvailabilityRefresh(router);
    });
  }

  function deleteWindow(windowId: string | null) {
    if (readOnly) return;
    if (!windowId) {
      setStatus({ kind: "error", message: "Window not found." });
      return;
    }

    startTransition(async () => {
      const result = await deleteCompanionAvailabilityWindow(windowId);
      if (!result.success) {
        setStatus({ kind: "error", message: result.error });
        return;
      }

      setStatus({ kind: "success", message: "Window deleted." });
      queueAvailabilityRefresh(router);
    });
  }

  return (
    <details
      data-availability-surface={surface}
      className={`mt-4 rounded-sm border ${classes.details}`}
    >
      <summary className={`flex min-h-[52px] cursor-pointer list-none items-center justify-between gap-3 px-4 py-3 outline-none focus-visible:ring-2 focus-visible:ring-inset ${classes.summary}`}>
        <span>
          <span className={`block text-[11px] font-semibold uppercase tracking-[1.5px] ${classes.eyebrow}`}>
            Settings
          </span>
          <h3 id="availability-heading" className="font-serif text-[22px] leading-tight">
            Saved planning hints
          </h3>
        </span>
        <ChevronDown className={`h-4 w-4 ${classes.icon}`} aria-hidden="true" />
      </summary>

      <div className={`space-y-4 border-t px-4 py-4 ${classes.body}`}>
        {windows.length > 0 ? (
          <ul className="space-y-2" aria-labelledby="availability-heading">
            {windows.map((availabilityWindow) => {
              const label =
                availabilityWindow.label?.trim() ||
                availabilityWindow.sourceText?.trim() ||
                "Saved window";
              return (
                <li
                  key={availabilityWindow.id ?? `${label}-${availabilityWindow.createdAt ?? "window"}`}
                  className={`rounded-sm border px-3 py-3 ${classes.item}`}
                >
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0">
                      <p className={`text-[13px] font-semibold leading-snug ${classes.itemTitle}`}>
                        {label}
                      </p>
                      <span className={`mt-2 inline-flex rounded-full border px-2.5 py-1 text-[11px] font-semibold ${classes.kind}`}>
                        {availabilityKindLabel(availabilityWindow.kind)}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <label className={`inline-flex min-h-[44px] cursor-pointer items-center gap-2 rounded-sm border px-3 py-2 text-[12px] font-semibold outline-none focus-within:ring-2 ${classes.toggle}`}>
                        <input
                          type="checkbox"
                          checked={availabilityWindow.active}
                          onChange={(event) =>
                            toggleWindow(availabilityWindow.id, event.target.checked)
                          }
                          disabled={readOnly || pending}
                          className="h-4 w-4 accent-gold"
                        />
                        Active
                      </label>
                      <button
                        type="button"
                        onClick={() => deleteWindow(availabilityWindow.id)}
                        disabled={readOnly || pending}
                        className={`inline-flex min-h-[44px] items-center justify-center gap-2 rounded-sm border px-3 py-2 text-[12px] font-semibold outline-none transition-colors focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-60 motion-reduce:transition-none ${classes.delete}`}
                      >
                        <Trash2 className="h-4 w-4" aria-hidden="true" />
                        Delete
                      </button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className={`rounded-sm border px-3 py-3 text-[12.5px] leading-relaxed ${classes.empty}`}>
            {sourceState === "unavailable"
              ? "Saved windows could not be loaded. Existing windows may still be saved."
              : "No saved windows yet."}
          </p>
        )}

        <p className={`rounded-sm border px-3 py-3 text-[12px] leading-relaxed ${classes.help}`}>
          These notes are hints, not confirmed free-time intervals. Review exact dates, times and commitments in Free time. An explicit saved travel limit still caps nearby travel.
        </p>
        <Link href="/companion/plan/free-time" className={`inline-flex min-h-11 items-center text-sm font-semibold underline ${classes.itemTitle}`}>Review exact free time</Link>

        <form
          className="space-y-3"
          onSubmit={(event) => {
            event.preventDefault();
            saveWindow();
          }}
        >
          <label htmlFor={inputId} className={`block text-[12px] font-semibold ${classes.label}`}>
            Add a window
          </label>
          <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto]">
            <input
              id={inputId}
              value={value}
              disabled={readOnly}
              onChange={(event) => setValue(event.target.value)}
              placeholder="Saturday morning"
              className="min-h-[44px] rounded-sm border border-white/12 bg-white px-3 text-[14px] text-navy outline-none transition-colors placeholder:text-text-muted/60 focus:border-gold"
              maxLength={240}
            />
            <button
              type="submit"
              disabled={readOnly || pending}
              className="inline-flex min-h-[44px] items-center justify-center rounded-sm bg-gold px-4 py-2 text-[13px] font-semibold text-navy transition-colors hover:bg-gold-light disabled:cursor-wait disabled:bg-gold/55"
            >
              {pending ? "Saving" : "Save window"}
            </button>
          </div>
          <p
            aria-live="polite"
            aria-atomic="true"
            className={`text-[12.5px] leading-relaxed ${
              status.kind === "error"
                ? classes.error
                : status.kind === "success"
                  ? classes.success
                  : classes.idle
            }`}
          >
            {status.message}
          </p>
        </form>
      </div>
    </details>
  );
}

function availabilityKindLabel(kind: CompanionAvailabilityWindow["kind"]): string {
  if (kind === "day-window") return "Day window";
  if (kind === "after-work") return "After work";
  if (kind === "travel-time") return "Travel time";
  if (kind === "free-text") return "Note";
  return "Window";
}

function queueAvailabilityRefresh(router: ReturnType<typeof useRouter>) {
  window.setTimeout(() => {
    router.refresh();
  }, 500);
}
